import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Card, Badge, EmptyState } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const emptyForm = () => ({
  name: "", email: "", department_id: "", role: "",
  joining_date: new Date().toISOString().slice(0, 10), status: "Active", remarks: "",
});

export default function Team() {
  const { departments, deptName, selectedDept } = useApp();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/team-members", { params: { department_id: selectedDept !== "all" ? selectedDept : undefined } })
      .then((r) => setMembers(r.data)).catch(() => {});
  }, [selectedDept]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm()); setEditId(null); setOpen(true); };
  const openEdit = (m) => { setForm({ ...emptyForm(), ...m }); setEditId(m.id); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.department_id) { toast.error("Name and department are required."); return; }
    try {
      if (editId) await api.put(`/team-members/${editId}`, form);
      else await api.post("/team-members", form);
      toast.success(editId ? "Member updated." : "Member added.");
      setOpen(false); load();
    } catch (e) { toast.error("Failed to save member."); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this member and their readiness records? This cannot be undone.")) return;
    await api.delete(`/team-members/${id}`); toast.success("Member removed."); load();
  };

  return (
    <div>
      <PageHeader
        title="Team Member Master List"
        subtitle="Standalone source of truth — add a member and they appear across all modules instantly."
        action={<Button data-testid="add-member-button" onClick={openNew} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f] gap-2"><Plus className="h-4 w-4" /> Add Member</Button>}
      />
      <Card className="p-0 overflow-hidden">
        {members.length === 0 ? (
          <EmptyState icon={Users} title="No team members" hint="Add your first team member and assign a department." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead>Employee ID</TableHead><TableHead>Name</TableHead><TableHead>Department</TableHead>
                <TableHead>Role</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((m) => (
                <TableRow key={m.id} data-testid={`member-row-${m.id}`} className="border-slate-800 hover:bg-slate-800/40">
                  <TableCell className="font-mono text-[#C9A24B] text-sm">{m.employee_id}</TableCell>
                  <TableCell className="text-white">{m.name}</TableCell>
                  <TableCell className="text-slate-300 text-sm">{deptName(m.department_id)}</TableCell>
                  <TableCell className="text-slate-400 text-sm">{m.role || "—"}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{m.email || "—"}</TableCell>
                  <TableCell><Badge tone={m.status === "Active" ? "green" : "slate"}>{m.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <button onClick={() => openEdit(m)} className="text-slate-400 hover:text-[#C9A24B] mr-3" data-testid={`edit-member-${m.id}`}><Pencil className="h-4 w-4 inline" /></button>
                    <button onClick={() => remove(m.id)} className="text-slate-400 hover:text-[#DC2626]" data-testid={`delete-member-${m.id}`}><Trash2 className="h-4 w-4 inline" /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F172A] border-slate-800 max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit Member" : "Add Team Member"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border-slate-700" data-testid="member-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Department</Label>
                <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                  <SelectTrigger data-testid="member-department" className="border-slate-700"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Role / Designation</Label><Input data-testid="member-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border-slate-700" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Google Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border-slate-700" data-testid="member-email" /></div>
              <div><Label>Joining Date</Label><Input type="date" value={form.joining_date || ""} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} className="border-slate-700" /></div>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Remarks</Label><Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="border-slate-700" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-member-button" onClick={save} className="bg-[#C9A24B] text-[#0B1120] hover:bg-[#b8923f]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
